import { faListUl } from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper, DetailInfo, DetailRow } from '@labrav/common'
import { useStyles } from '../ViewMicrosoftComponent.styles'
import { dateAndTimeConversion } from '../../../../../common/utils/dateAndTimeConversion'
import { useSelector } from '../../../../../store'
import { get } from 'lodash'
import { ViewValueWithTruncate, ColumnReadMore } from '@labrav/common'
import type { ViewResellerAuthorizationType } from '../../../../../modules/receivedResellerAuthorization/types'
import { AzurePricingType } from '../../../../../modules/privateOffer/reducer'

export const MicrosoftResellerAuthorizationDetails = ({
  resaleAuthorization,
}: {
  resaleAuthorization?: ViewResellerAuthorizationType
}) => {
  const { classes } = useStyles()
  const timeZone = useSelector(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )

  return (
    <div>
      <SectionWrapper heading="Reseller authorization details" icon={faListUl}>
        <div className={classes.sectionContent}>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Buyer name"
                value={
                  <ViewValueWithTruncate
                    value={resaleAuthorization?.companyName}
                  />
                }
                keyName="cloudOfferId"
              />
              <DetailInfo
                title="Buyer billing account ID"
                value={
                  <ViewValueWithTruncate
                    value={resaleAuthorization?.beneficiaryId}
                    showIcon={true}
                  />
                }
                keyName="labraOfferId"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Offer name"
                value={
                  <ViewValueWithTruncate
                    value={resaleAuthorization?.mppoName}
                  />
                }
                keyName="mppoName"
              />
              <DetailInfo
                title="Authorization expiration date"
                value={
                  resaleAuthorization?.acceptBy
                    ? (() => {
                        const { modifiedDate, modifiedTime } =
                          dateAndTimeConversion(
                            resaleAuthorization?.acceptBy,
                            timeZone
                          )
                        return (
                          <div className={classes.dateTimeWrapper}>
                            <div className={classes.createdAtDate}>
                              {modifiedDate}
                            </div>
                            {modifiedTime && (
                              <div className={classes.createdAtTime}>
                                {modifiedTime.trim()}
                              </div>
                            )}
                          </div>
                        )
                      })()
                    : '-'
                }
                keyName="acceptBy"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Offer description"
                value={
                  resaleAuthorization?.mppoDescription ? (
                    <ColumnReadMore
                      customCharForRemoveFromMax={15}
                      initializeWithLowerCase
                      description={resaleAuthorization?.mppoDescription}
                    />
                  ) : (
                    '-'
                  )
                }
                keyName="mppoDescription"
                gridSize={12}
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Reseller name"
                value={
                  <ViewValueWithTruncate
                    value={get(
                      resaleAuthorization,
                      'resellerDetails[0].partnerName',
                      ''
                    )}
                  />
                }
                keyName="resellerDetails[0].partnerName"
              />
              <DetailInfo
                title="Reseller account ID"
                value={
                  <ViewValueWithTruncate
                    showIcon={true}
                    value={get(
                      resaleAuthorization,
                      'resellerDetails[0].id',
                      ''
                    )}
                  />
                }
                keyName="resellerDetails[0].id"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Contract start date"
                value={
                  resaleAuthorization?.variableStartDate
                    ? 'Accepted date'
                    : resaleAuthorization?.offerStartDate
                      ? (() => {
                          const { modifiedDate, modifiedTime } =
                            resaleAuthorization?.offerStartDate
                              ? dateAndTimeConversion(
                                  resaleAuthorization?.offerStartDate,
                                  timeZone
                                )
                              : { modifiedDate: '', modifiedTime: '' }
                          return (
                            <div className={classes.dateTimeWrapper}>
                              <div className={classes.createdAtDate}>
                                {modifiedDate || '-'}
                              </div>
                              {modifiedTime && (
                                <div className={classes.createdAtTime}>
                                  {modifiedTime.trim()}
                                </div>
                              )}
                            </div>
                          )
                        })()
                      : '-'
                }
                keyName="offerStartDate"
              />
              <DetailInfo
                title="Contract end date"
                value={
                  resaleAuthorization?.offerExpirationDate
                    ? (() => {
                        const { modifiedDate, modifiedTime } =
                          dateAndTimeConversion(
                            resaleAuthorization?.offerExpirationDate,
                            timeZone
                          )
                        return (
                          <div className={classes.dateTimeWrapper}>
                            <div className={classes.createdAtDate}>
                              {modifiedDate || '-'}
                            </div>
                            {modifiedTime && (
                              <div className={classes.createdAtTime}>
                                {modifiedTime.trim()}
                              </div>
                            )}
                          </div>
                        )
                      })()
                    : '-'
                }
                keyName="offerExpirationDate"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Auto renewal"
                value={
                  resaleAuthorization?.existingContractRenewal ? 'Yes' : 'No'
                }
                keyName="existingContractRenewal"
              />
              <DetailInfo
                title="Pricing configuration"
                value={
                  resaleAuthorization?.azurePricingType
                    ? resaleAuthorization?.azurePricingType ===
                      AzurePricingType?.ABSOLUTE
                      ? 'Absolute'
                      : 'Discount'
                    : '-'
                }
                keyName="azurePricingType"
              />
            </>
          </DetailRow>
        </div>
      </SectionWrapper>
    </div>
  )
}
